import React, { useRef, useEffect, useCallback, useState } from 'react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { MapPin, AlertCircle } from 'lucide-react';

/**
 * Map Google's province names to our standard province names
 */
const mapProvince = (googleProvince) => {
  if (!googleProvince) return '';
  
  const provinceMap = {
    'gauteng': 'Gauteng',
    'western cape': 'Western Cape',
    'eastern cape': 'Eastern Cape',
    'kwazulu-natal': 'KwaZulu-Natal',
    'kzn': 'KwaZulu-Natal',
    'free state': 'Free State',
    'limpopo': 'Limpopo',
    'mpumalanga': 'Mpumalanga',
    'north west': 'North West',
    'northern cape': 'Northern Cape'
  };
  
  const normalized = googleProvince.toLowerCase().trim();
  return provinceMap[normalized] || googleProvince;
};

/**
 * Parse address components from Google Places API (new format)
 * Supports both old (long_name) and new (longText) API formats
 */
const parseAddressComponents = (addressComponents) => {
  const result = {
    street: '',
    city: '',
    province: '',
    postal_code: '',
    country: ''
  };

  if (!addressComponents) {
    console.log('No address components provided');
    return result;
  }

  console.log('Parsing address components:', addressComponents);

  let streetNumber = '';
  let route = '';
  let sublocality = '';

  addressComponents.forEach((component) => {
    const types = component.types || [];
    // Support both new (longText) and old (long_name) API formats
    const value = component.longText || component.long_name || '';
    
    console.log('Component:', types, value);
    
    if (types.includes('street_number')) {
      streetNumber = value;
    }
    if (types.includes('route')) {
      route = value;
    }
    if (types.includes('sublocality') || types.includes('sublocality_level_1')) {
      sublocality = value;
    }
    if (types.includes('locality')) {
      result.city = value;
    }
    if (types.includes('administrative_area_level_1')) {
      result.province = mapProvince(value);
    }
    if (types.includes('postal_code')) {
      result.postal_code = value;
    }
    if (types.includes('country')) {
      result.country = value;
    }
  });

  // Combine street number and route
  result.street = streetNumber ? `${streetNumber} ${route}`.trim() : route;
  
  // If no locality found, use sublocality
  if (!result.city && sublocality) {
    result.city = sublocality;
  }
  
  console.log('Parsed result:', result);
  return result;
};

const AddressAutocomplete = ({ value, onChange, onPlaceSelect, label, required = false, placeholder, className }) => {
  const containerRef = useRef(null);
  const autocompleteElementRef = useRef(null);
  const [mapsError, setMapsError] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const onChangeRef = useRef(onChange);
  const onPlaceSelectRef = useRef(onPlaceSelect);

  // Keep refs updated
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    onPlaceSelectRef.current = onPlaceSelect;
  }, [onPlaceSelect]);

  useEffect(() => {
    // Prevent double initialization
    if (isInitialized || !containerRef.current) return;

    const initAutocomplete = async () => {
      try {
        // Check if Google Maps API is loaded
        if (!window.google || !window.google.maps) {
          console.warn('Google Maps API not loaded');
          setMapsError(true);
          return;
        }

        // Import the places library using the new API
        const { PlaceAutocompleteElement } = await window.google.maps.importLibrary('places');

        if (!PlaceAutocompleteElement) {
          console.warn('PlaceAutocompleteElement not available');
          setMapsError(true);
          return;
        }

        // Create the new PlaceAutocompleteElement
        const autocomplete = new PlaceAutocompleteElement({
          componentRestrictions: { country: 'za' }, // Restrict to South Africa
        });

        // Style the element to match our dark theme
        autocomplete.style.cssText = `
          --gmpx-color-surface: #374151;
          --gmpx-color-on-surface: #ffffff;
          --gmpx-color-on-surface-variant: #9ca3af;
          --gmpx-color-primary: #eab308;
          --gmpx-color-outline: #4b5563;
          width: 100%;
          font-family: inherit;
        `;

        // Add placeholder
        autocomplete.setAttribute('placeholder', placeholder || 'Start typing an address...');

        // Listen for place selection using gmp-select (new API - gmp-placeselect was renamed)
        autocomplete.addEventListener('gmp-select', async ({ placePrediction }) => {
          try {
            console.log('Place selected (gmp-select):', placePrediction);
            if (!placePrediction) {
              console.warn('No place prediction in event');
              return;
            }

            const place = placePrediction.toPlace();
            console.log('Place object:', place);
            
            // Fetch the full place details
            await place.fetchFields({
              fields: ['displayName', 'formattedAddress', 'addressComponents', 'location']
            });

            console.log('Place details fetched:', {
              displayName: place.displayName,
              formattedAddress: place.formattedAddress,
              addressComponents: place.addressComponents,
              location: place.location
            });

            // Update the display value
            if (place.formattedAddress) {
              onChangeRef.current(place.formattedAddress);
            }

            // Parse and send address components
            if (onPlaceSelectRef.current && place.addressComponents) {
              const parsedAddress = parseAddressComponents(place.addressComponents);
              console.log('Calling onPlaceSelect with:', parsedAddress);
              onPlaceSelectRef.current(parsedAddress);
            } else {
              console.warn('onPlaceSelect callback not available or no addressComponents', {
                hasCallback: !!onPlaceSelectRef.current,
                hasComponents: !!place.addressComponents
              });
            }
          } catch (error) {
            console.error('Error fetching place details:', error);
          }
        });

        // Also listen to legacy event name for backward compatibility
        autocomplete.addEventListener('gmp-placeselect', async (event) => {
          try {
            console.log('Place selected (gmp-placeselect legacy):', event);
            const placePrediction = event.placePrediction;
            if (!placePrediction) return;

            const place = placePrediction.toPlace();
            await place.fetchFields({
              fields: ['displayName', 'formattedAddress', 'addressComponents', 'location']
            });

            if (place.formattedAddress) {
              onChangeRef.current(place.formattedAddress);
            }

            if (onPlaceSelectRef.current && place.addressComponents) {
              const parsedAddress = parseAddressComponents(place.addressComponents);
              onPlaceSelectRef.current(parsedAddress);
            }
          } catch (error) {
            console.error('Error in legacy event handler:', error);
          }
        });

        // Clear the container and append the element
        if (containerRef.current) {
          containerRef.current.innerHTML = '';
          containerRef.current.appendChild(autocomplete);
          autocompleteElementRef.current = autocomplete;
          setIsInitialized(true);
        }

      } catch (error) {
        console.error('Error initializing PlaceAutocompleteElement:', error);
        setMapsError(true);
      }
    };

    // Small delay to ensure Google Maps script is fully loaded
    const timeoutId = setTimeout(initAutocomplete, 100);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [isInitialized, placeholder]);

  // Handle manual input changes (fallback)
  const handleInputChange = useCallback((e) => {
    onChange(e.target.value);
  }, [onChange]);

  return (
    <div className={className}>
      {label && (
        <Label htmlFor="address-autocomplete" className="text-gray-300">
          {label} {required && <span className="text-red-500">*</span>}
        </Label>
      )}
      
      {!mapsError ? (
        <>
          {/* Google PlaceAutocompleteElement container - element has its own icon */}
          <div className="relative mt-1">
            <div 
              ref={containerRef} 
              className="w-full [&>gmpx-place-autocomplete]:w-full"
              data-testid="address-autocomplete-container"
            />
          </div>
          <p className="text-xs text-gray-500 mt-1">
            Start typing to see address suggestions
          </p>
        </>
      ) : (
        <>
          {/* Fallback manual input */}
          <div className="relative mt-1">
            <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-yellow-500 z-10" />
            <Input
              id="address-autocomplete"
              type="text"
              value={value}
              onChange={handleInputChange}
              placeholder={placeholder || "Enter address manually..."}
              className="pl-10 bg-gray-700 border-gray-600 text-white placeholder:text-gray-400"
              required={required}
              data-testid="address-autocomplete-input"
            />
          </div>
          <p className="text-xs text-amber-500 mt-1 flex items-center gap-1">
            <AlertCircle className="h-3 w-3" />
            Address autocomplete unavailable. Please enter address manually below.
          </p>
        </>
      )}
    </div>
  );
};

export default AddressAutocomplete;
